import { useCanvasStore } from '../store/canvas-store';
import { isPanelOwned, type Frame } from '../store/types';
import { resolveCfNode } from '../store/slot-schema';
import { noteTargetPoint, type RectLookup } from './noteTarget';

/**
 * Overlay drawing dotted leaders from tool frames to their anchors, plus
 * note leaders + numbered pins for all 3 target kinds. Rendered inside the
 * world-transformed container so coords are canvas world space.
 *
 * chat → green, code → amber, fx → purple, note → teal, missing → red.
 */
export function AnchorEdges() {
  const frames = useCanvasStore((s) => s.doc.frames);
  const annotationsVisible = useCanvasStore((s) => s.annotationsVisible);
  const byId = new Map(frames.map((f) => [f.id, f] as const));

  // Live DOM lookup for sub-element targets. data-frame-id wraps each frame;
  // injectCfNodeIds stamps data-cf-id on slot/structural nodes.
  const rectLookup: RectLookup = (frameId, nodeId) => {
    if (typeof document === 'undefined') return null;
    const host = document.querySelector(`[data-frame-id="${cssEscape(frameId)}"]`);
    const el = host ? resolveCfNode(host, nodeId) : null;
    const frame = byId.get(frameId);
    if (!el || !frame) return null;
    const hostRect = (host as HTMLElement).getBoundingClientRect();
    const elRect = (el as HTMLElement).getBoundingClientRect();
    const zoom = useCanvasStore.getState().doc.viewport.zoom || 1;
    return {
      x: frame.x + (elRect.left - hostRect.left) / zoom,
      y: frame.y + (elRect.top - hostRect.top) / zoom,
      w: elRect.width / zoom,
      h: elRect.height / zoom,
    };
  };

  const edges: { fromX: number; fromY: number; toX: number; toY: number; color: string; missing: boolean }[] = [];
  const pins: { x: number; y: number; label: string; color: string }[] = [];

  for (const tool of frames) {
    if (isPanelOwned(tool)) continue; // panel-owned frames draw no leader
    if (tool.kind === 'chat' || tool.kind === 'code' || tool.kind === 'fx') {
      if (!tool.anchorId) continue;
      const anchor = byId.get(tool.anchorId);
      const color = tool.kind === 'chat' ? 'var(--color-tool-chat)' : tool.kind === 'code' ? 'var(--color-tool-code)' : 'var(--color-tool-fx)';
      const from = frameCenter(tool);
      if (!anchor) {
        edges.push({ fromX: from.x, fromY: from.y, toX: from.x + 40, toY: from.y - 20, color: 'var(--color-danger)', missing: true });
        continue;
      }
      const to = frameCenter(anchor);
      edges.push({ fromX: from.x, fromY: from.y, toX: to.x, toY: to.y, color, missing: false });
      continue;
    }
    if (tool.kind === 'note' && annotationsVisible) {
      const pt = noteTargetPoint(tool, byId, rectLookup);
      if (!pt) continue;
      const from = frameCenter(tool);
      const accent = tool.color ?? 'var(--color-tool-note)';
      edges.push({ fromX: from.x, fromY: from.y, toX: pt.x, toY: pt.y, color: accent, missing: false });
      if (tool.target && tool.target.kind !== 'frame') {
        pins.push({ x: pt.x, y: pt.y, label: tool.step !== undefined ? String(tool.step) : '•', color: accent });
      }
    }
  }

  if (edges.length === 0 && pins.length === 0) return null;

  return (
    <svg style={{ position: 'absolute', left: 0, top: 0, width: 1, height: 1, overflow: 'visible', pointerEvents: 'none' }}>
      {edges.map((e, i) => (
        <line
          key={`e${i}`}
          x1={e.fromX} y1={e.fromY} x2={e.toX} y2={e.toY}
          stroke={e.color} strokeWidth={1}
          strokeDasharray={e.missing ? '6 3' : '3 3'}
          opacity={e.missing ? 0.9 : 0.5}
        />
      ))}
      {pins.map((p, i) => (
        <g key={`p${i}`}>
          <circle cx={p.x} cy={p.y} r={11} fill={p.color} />
          <text x={p.x} y={p.y + 4} textAnchor="middle" fontSize={12} fontWeight={700} fill="#042f2a">{p.label}</text>
        </g>
      ))}
    </svg>
  );
}

function frameCenter(f: Frame): { x: number; y: number } {
  return { x: f.x + f.w / 2, y: f.y + (f.h ?? 80) / 2 };
}

function cssEscape(v: string): string {
  return v.replace(/"/g, '\\"');
}
